import Decimal from 'decimal.js';
import type { CustomerBalanceResponse, LoyaltyPolicyResponse } from '../../model/loyalty/loyalty';

const MAX_FRACTION_DIGITS = 20;

/**
 * Credit the current cart earns at the shopper's tier, matching checkout's earning basis:
 * cart total after discounts, floored to whole points, then valued in currency.
 * Null means the policy cannot price this cart (missing tier, invalid rates, or nothing earned).
 */
export function getCartEarnedCredit({
  cartTotal,
  balance,
  policy,
}: {
  cartTotal: number;
  balance: Pick<CustomerBalanceResponse, 'tier' | 'fractionDigits'>;
  policy: Pick<LoyaltyPolicyResponse, 'tierPointInfo' | 'pointValueInCurrency' | 'fractionDigits'>;
}): { credit: number; fractionDigits: number } | null {
  const tier = policy.tierPointInfo?.find(({ tier: name }) => name === balance.tier);
  if (!tier) {
    return null;
  }
  const hasValidPolicy = [cartTotal, tier.pointsPerCurrency, tier.pointMultiplier, policy.pointValueInCurrency].every(
    (value) => Number.isFinite(value) && value >= 0,
  );
  if (!hasValidPolicy) {
    return null;
  }
  const points = Math.floor(cartTotal * tier.pointsPerCurrency * tier.pointMultiplier);
  const credit = new Decimal(points).times(policy.pointValueInCurrency).toNumber();
  if (!Number.isFinite(credit) || credit <= 0) {
    return null;
  }
  const fractionDigits = balance.fractionDigits ?? policy.fractionDigits;
  if (!Number.isInteger(fractionDigits) || fractionDigits < 0 || fractionDigits > MAX_FRACTION_DIGITS) {
    return null;
  }
  return { credit, fractionDigits };
}
